import {
  Brush as BrushIcon,
  CloudDone as CloudIcon,
  FlashOn as FlashIcon,
  Psychology as BrainIcon,
  Rocket as RocketIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingIcon,
  Visibility as VisibilityIcon,
  ArrowForward as ArrowIcon,
  Star as StarIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Chip
} from '@mui/material';
import { useEffect, useState } from 'react';

// Import images
import featuresImage from '../assets/flow.webp';

const FeaturesPremium = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Premium feature data with enhanced content
  const features = [
    {
      id: 1,
      icon: <BrainIcon />,
      title: "AI Destekli İçerik",
      subtitle: "Yapay Zeka ile Hızlı Üretim",
      description: "GPT-4 entegrasyonu ile gelişmiş AI teknolojisi. Otomatik içerik üretimi, SEO optimizasyonu ve akıllı öneriler.",
      details: [
        "GPT-4 API entegrasyonu",
        "Otomatik SEO skorlama", 
        "Akıllı başlık önerileri",
        "İçerik performans analizi"
      ],
      color: "#4f46e5",
      gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
      lightColor: "#e0e7ff",
      darkColor: "#312e81",
      badge: "AI Powered",
      rating: 4.9
    },
    {
      id: 2,
      icon: <FlashIcon />,
      title: "Ultra Hızlı Performans",
      subtitle: "Lightning Speed Technology",
      description: "Edge computing ve CDN teknolojisi ile 150ms altı yükleme süreleri. Global performans optimizasyonu.",
      details: [
        "150ms altı yükleme süresi",
        "Global CDN ağı",
        "Edge computing desteği",
        "Otomatik cache yönetimi"
      ],
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)",
      lightColor: "#fef3c7",
      darkColor: "#92400e",
      badge: "Ultra Fast",
      rating: 4.8
    },
    {
      id: 3,
      icon: <SecurityIcon />,
      title: "Enterprise Güvenlik",
      subtitle: "Military Grade Protection",
      description: "SOC 2 Type II uyumlu güvenlik altyapısı. End-to-end şifreleme ve çok katmanlı güvenlik duvarı.",
      details: [
        "SOC 2 Type II uyumluluk",
        "End-to-end şifreleme",
        "Biometric authentication",
        "24/7 güvenlik izleme"
      ],
      color: "#059669",
      gradient: "linear-gradient(135deg, #059669 0%, #0891b2 100%)",
      lightColor: "#d1fae5",
      darkColor: "#064e3b",
      badge: "Enterprise",
      rating: 5.0
    },
    {
      id: 4,
      icon: <BrushIcon />,
      title: "Advanced Design System",
      subtitle: "Professional Creative Tools",
      description: "Adobe Creative Suite entegrasyonu ile profesyonel tasarım araçları. 10,000+ premium şablon ve asset.",
      details: [
        "Adobe CC entegrasyonu",
        "10,000+ premium şablon",
        "Advanced color system",
        "Professional typography"
      ],
      color: "#7c3aed",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)",
      lightColor: "#ede9fe",
      darkColor: "#581c87",
      badge: "Creative Pro",
      rating: 4.7
    },
    {
      id: 5,
      icon: <TrendingIcon />,
      title: "Advanced Analytics",
      subtitle: "Business Intelligence Platform",
      description: "Machine learning destekli analitik platform. Predictive analytics ve real-time business insights.",
      details: [
        "Machine learning insights",
        "Predictive analytics",
        "Real-time dashboards",
        "Custom reporting API"
      ],
      color: "#dc2626",
      gradient: "linear-gradient(135deg, #dc2626 0%, #ea580c 100%)",
      lightColor: "#fee2e2",
      darkColor: "#991b1b",
      badge: "Analytics Pro",
      rating: 4.6
    },
    {
      id: 6,
      icon: <CloudIcon />,
      title: "Multi-Cloud Infrastructure",
      subtitle: "Global Scale Technology",
      description: "AWS, Azure ve GCP üzerinde multi-cloud altyapı. 99.99% uptime garantisi ve otomatik failover.",
      details: [
        "Multi-cloud deployment",
        "99.99% uptime SLA",
        "Auto-scaling infrastructure",
        "Global load balancing"
      ],
      color: "#0891b2",
      gradient: "linear-gradient(135deg, #0891b2 0%, #059669 100%)",
      lightColor: "#cffafe",
      darkColor: "#164e63",
      badge: "Enterprise Cloud",
      rating: 4.9
    }
  ];

  // Mouse tracking for interactive effects
  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  // Enhanced intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setTimeout(() => {
              setVisibleCards(prev => new Set([...prev, index]));
            }, index * 120);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const cards = document.querySelectorAll('[data-index]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Add mouse move listener
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <Box
      sx={{
        py: { xs: 12, md: 20 },
        background: `
          linear-gradient(180deg, #f8fafc 0%, #ffffff 30%, #f1f5f9 70%, #e2e8f0 100%),
          radial-gradient(circle at 25% 25%, rgba(79, 70, 229, 0.03) 0%, transparent 50%),
          radial-gradient(circle at 75% 75%, rgba(236, 72, 153, 0.03) 0%, transparent 50%)
        `,
        color: '#0f172a',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            conic-gradient(from 0deg at 20% 20%, rgba(79, 70, 229, 0.02), transparent, rgba(79, 70, 229, 0.02)),
            conic-gradient(from 90deg at 80% 80%, rgba(236, 72, 153, 0.02), transparent, rgba(236, 72, 153, 0.02))
          `,
          pointerEvents: 'none'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 300,
          height: 300,
          background: `radial-gradient(circle, rgba(79, 70, 229, 0.05) 0%, transparent 70%)`,
          borderRadius: '50%',
          left: mousePosition.x - 150,
          top: mousePosition.y - 150,
          pointerEvents: 'none',
          transition: 'all 0.3s ease',
          opacity: 0.6
        }
      }}
    >
      <Container maxWidth="xl">
        {/* Premium Header */}
        <Box sx={{ textAlign: 'center', mb: 12, position: 'relative' }}>
          <Chip
            icon={<StarIcon />}
            label="Premium Features"
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.9rem',
              px: 2,
              py: 0.5,
              '& .MuiChip-icon': {
                color: 'white'
              }
            }}
          />

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '3rem', md: '5rem' },
              fontWeight: 900,
              mb: 4,
              background: `
                linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #4f46e5 60%, #7c3aed 100%)
              `,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
              lineHeight: 0.9,
              position: 'relative',
              textShadow: '0 0 40px rgba(79, 70, 229, 0.3)',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 120,
                height: 6,
                background: 'linear-gradient(90deg, #4f46e5, #7c3aed, #ec4899)',
                borderRadius: 3,
                animation: 'shimmer 2s ease-in-out infinite'
              }
            }}
          >
            Next-Gen Solutions
          </Typography>

          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.4rem', md: '1.8rem' },
              color: '#475569',
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 400,
              mb: 2
            }}
          >
            Enterprise seviyesinde teknoloji ve kullanıcı deneyimi ile projelerinizi geleceğe taşıyın
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#64748b',
              fontSize: '1.1rem',
              fontWeight: 500
            }}
          >
            ⚡ Trusted by 10,000+ companies worldwide
          </Typography>
        </Box>

        {/* Premium Features Grid */}
        <Grid container spacing={5} sx={{ mb: 16 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={feature.id}>
              <Paper
                data-index={index}
                elevation={0}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{
                  p: 6,
                  height: '100%',
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(226, 232, 240, 0.8)',
                  borderRadius: 5,
                  position: 'relative',
                  cursor: 'pointer',
                  opacity: visibleCards.has(index) ? 1 : 0,
                  transform: visibleCards.has(index) 
                    ? 'translateY(0) scale(1) rotateX(0deg)' 
                    : 'translateY(40px) scale(0.9) rotateX(10deg)',
                  transition: 'all 0.8s cubic-bezier(0.23, 1, 0.32, 1)',
                  transformStyle: 'preserve-3d',
                  perspective: 1000,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 5,
                    background: feature.gradient,
                    transform: hoveredCard === index ? 'scaleX(1)' : 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'transform 0.5s cubic-bezier(0.23, 1, 0.32, 1)'
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -100,
                    right: -100,
                    width: 200,
                    height: 200,
                    background: `conic-gradient(from 0deg, ${feature.color}10, transparent, ${feature.color}10)`,
                    borderRadius: '50%',
                    opacity: hoveredCard === index ? 1 : 0,
                    transition: 'all 0.6s ease',
                    animation: hoveredCard === index ? 'spin 8s linear infinite' : 'none',
                    pointerEvents: 'none'
                  },
                  '&:hover': {
                    transform: 'translateY(-12px) scale(1.03) rotateX(-2deg)',
                    boxShadow: `
                      0 25px 50px ${feature.color}15,
                      0 15px 30px rgba(0, 0, 0, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.6)
                    `,
                    borderColor: feature.color,
                    background: 'rgba(255, 255, 255, 0.95)',
                    '& .feature-icon': {
                      transform: 'scale(1.15) rotate(10deg) translateZ(20px)',
                      background: feature.gradient,
                      boxShadow: `0 10px 30px ${feature.color}40`
                    },
                    '& .feature-button': {
                      transform: 'translateX(12px)',
                      color: feature.color
                    },
                    '& .feature-rating': {
                      transform: 'scale(1.1)',
                      color: feature.color
                    }
                  }
                }}
              >
                {/* Premium Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    background: feature.lightColor,
                    color: feature.color,
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    border: `1px solid ${feature.color}30`
                  }}
                >
                  {feature.badge}
                </Box>

                {/* Enhanced Feature Icon */}
                <Box
                  className="feature-icon"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 5,
                    width: 90,
                    height: 90,
                    background: feature.lightColor,
                    borderRadius: 4,
                    color: feature.color,
                    position: 'relative',
                    transition: 'all 0.6s cubic-bezier(0.23, 1, 0.32, 1)',
                    transformStyle: 'preserve-3d',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -2,
                      borderRadius: 4,
                      background: feature.gradient,
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      zIndex: -1
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 4,
                      background: feature.lightColor,
                      transition: 'all 0.3s ease'
                    }
                  }}
                >
                  <Box sx={{ fontSize: '2.5rem', zIndex: 2, position: 'relative' }}>
                    {feature.icon}
                  </Box>
                </Box>

                {/* Premium Content */}
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    color: '#0f172a',
                    fontSize: '1.6rem',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    lineHeight: 1.2
                  }}
                >
                  {feature.title}
                </Typography>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    background: feature.gradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: '1.2rem'
                  }}
                >
                  {feature.subtitle}
                </Typography>

                {/* Rating */}
                <Box
                  className="feature-rating"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 3,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <StarIcon
                        key={star}
                        sx={{
                          fontSize: '1rem',
                          color: star <= Math.floor(feature.rating) ? '#fbbf24' : '#e5e7eb'
                        }}
                      />
                    ))}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#475569',
                      fontSize: '0.9rem'
                    }}
                  >
                    {feature.rating}
                  </Typography>
                </Box>

                <Typography
                  variant="body1"
                  sx={{
                    color: '#475569',
                    lineHeight: 1.7,
                    mb: 5,
                    fontSize: '1.05rem'
                  }}
                >
                  {feature.description}
                </Typography>

                {/* Enhanced Feature List */}
                <Stack spacing={2.5} sx={{ mb: 6 }}>
                  {feature.details.map((detail, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5,
                        opacity: visibleCards.has(index) ? 1 : 0,
                        transform: visibleCards.has(index) ? 'translateX(0)' : 'translateX(-30px)',
                        transition: `all 0.6s cubic-bezier(0.23, 1, 0.32, 1) ${1 + idx * 0.15}s`
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: feature.gradient,
                          flexShrink: 0,
                          boxShadow: `0 0 10px ${feature.color}40`
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#334155',
                          fontSize: '1rem',
                          fontWeight: 500
                        }}
                      >
                        {detail}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {/* Premium Button */}
                <Button
                  className="feature-button"
                  variant="text"
                  endIcon={<ArrowIcon />}
                  sx={{
                    color: '#64748b',
                    fontWeight: 700,
                    px: 0,
                    py: 1.5,
                    fontSize: '1rem',
                    textTransform: 'none',
                    transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: 0,
                      height: 2,
                      background: feature.gradient,
                      transition: 'width 0.3s ease'
                    },
                    '& .MuiButton-endIcon': {
                      transition: 'transform 0.4s cubic-bezier(0.23, 1, 0.32, 1)'
                    },
                    '&:hover': {
                      background: 'transparent',
                      '&::before': {
                        width: '100%'
                      },
                      '& .MuiButton-endIcon': {
                        transform: 'translateX(8px) scale(1.2)'
                      }
                    }
                  }}
                >
                  Detayları Keşfet
                </Button>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Premium Call to Action */}
        <Box
          sx={{
            textAlign: 'center',
            py: 16,
            px: 8,
            background: `
              linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%),
              conic-gradient(from 0deg, rgba(79, 70, 229, 0.1), rgba(124, 58, 237, 0.1), rgba(236, 72, 153, 0.1), rgba(79, 70, 229, 0.1))
            `,
            backdropFilter: 'blur(20px)',
            borderRadius: 8,
            border: '1px solid rgba(226, 232, 240, 0.8)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 30% 30%, rgba(79, 70, 229, 0.08) 0%, transparent 50%),
                radial-gradient(circle at 70% 70%, rgba(124, 58, 237, 0.08) 0%, transparent 50%),
                linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)
              `,
              pointerEvents: 'none',
              animation: 'shimmer 3s ease-in-out infinite'
            }
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              mb: 5,
              background: `
                linear-gradient(135deg, #0f172a 0%, #4f46e5 50%, #7c3aed 100%)
              `,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              position: 'relative',
              textShadow: '0 0 40px rgba(79, 70, 229, 0.3)'
            }}
          >
            Geleceği Bugün Yaşayın!
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: '#475569',
              mb: 8,
              maxWidth: 700,
              mx: 'auto',
              fontSize: '1.3rem',
              fontWeight: 400,
              lineHeight: 1.6
            }}
          >
            Enterprise seviyesinde tüm özellikleri deneyimlemeye başlamak için sadece bir adım kaldı
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={4}
            sx={{ justifyContent: 'center', mb: 6 }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RocketIcon />}
              sx={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)',
                color: 'white',
                fontWeight: 800,
                px: 10,
                py: 4,
                fontSize: '1.2rem',
                borderRadius: 4,
                textTransform: 'none',
                boxShadow: '0 12px 40px rgba(79, 70, 229, 0.4)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: -100,
                  width: 100,
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  transition: 'left 0.8s ease',
                  animation: 'shimmer 2s ease-in-out infinite'
                },
                '&:hover': {
                  background: 'linear-gradient(135deg, #3730a3 0%, #6b21a8 50%, #be185d 100%)',
                  transform: 'translateY(-3px) scale(1.05)',
                  boxShadow: '0 20px 60px rgba(79, 70, 229, 0.5)',
                  '&::before': {
                    left: '100%'
                  }
                },
                transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)'
              }}
            >
              Premium'a Başla
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<VisibilityIcon />}
              sx={{
                borderColor: '#cbd5e1',
                color: '#475569',
                fontWeight: 700,
                px: 10,
                py: 4,
                fontSize: '1.2rem',
                borderRadius: 4,
                textTransform: 'none',
                borderWidth: 2,
                background: 'rgba(255, 255, 255, 0.5)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderColor: '#4f46e5',
                  color: '#4f46e5',
                  background: 'rgba(255, 255, 255, 0.8)',
                  transform: 'translateY(-3px) scale(1.05)',
                  boxShadow: '0 15px 40px rgba(79, 70, 229, 0.2)'
                },
                transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)'
              }}
            >
              Live Demo
            </Button>
          </Stack>

          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '1rem',
              fontWeight: 500,
              opacity: 0.8
            }}
          >
            🔥 14 günlük ücretsiz deneme • Kredi kartı gerekmez • Anında kurulum
          </Typography>
        </Box>
      </Container>

      {/* Enhanced CSS Animations */}
      <style jsx global>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(200%) skewX(-15deg); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }

        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(79, 70, 229, 0.3); }
          50% { box-shadow: 0 0 40px rgba(79, 70, 229, 0.6); }
        }
      `}</style>
    </Box>
  );
};

export default FeaturesPremium;
